import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { Box, Grid, ListItemText } from '@material-ui/core';
import { theme } from 'config/theme';
import i18n from 'i18n-js';
import { AnimatedCounter, BasicThreeDotsMenu, StyledChip } from 'components/shared';
import styles from '../../styles';
import { images } from 'assets';
import KPICard from '../../shared/kpiCard';
import DateFilters from '../DateFIlters';
import { DateHistoryData } from '../finances';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import agent from 'api/agent';
import moment from 'moment';
import financeStore from 'store/financeStore';
import PaymentReportTable from './components/paymentsTable';
import { ChartBarCheckbox, ChartDataItem, Charts } from '../finances/components/Charts';

interface PaymentsTabProps {
    restaurantId?: string;
    isOld?: boolean;
    style?: React.CSSProperties;
}

function PaymentsTab(props: PaymentsTabProps) {
    const { restaurantId, isOld: isAdminView } = props;
    const classes = styles();
    const dispatch = useDispatch();
    const report = useSelector((state: ApplicationState) => state.finance.paymentReport);
    const currency = useSelector(
        (state: ApplicationState) =>
            state.restaurantDetails?.restaurantInformation?.paymentDetails?.currency || 'CHF'
    );
    const [dateFilter, setDateFilter] = useState(0);
    const [chartView, setChartView] = useState(false);
    const [selectedDatesHistory, setSelectedDatesHistory] = useState<DateHistoryData[]>([]);
    const [hoveredSwitchIcon, setHoveredSwitchIcon] = useState(0);
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const paymentMethods = Array.from(new Set(report.transactions.map((x) => x.method)));
    const dataList: ChartDataItem[] = (report.transactions || []).map((x) => ({
        name: x.method,
        amount: x.amount,
        date: x.paymentDate,
    }));

    function getData(
        dayAgo: number,
        dateHistoryList?: DateHistoryData[] | undefined,
        dateFrom?: Date | undefined,
        dateTo?: Date | undefined
    ): void {
        var from = dateFrom ?? moment().subtract(1, 'day').toDate();
        var to = dateTo ?? moment().toDate();

        if (!dateFrom && !dateTo) {
            dateTo = new Date();
            dateFrom = moment().subtract(dayAgo, 'days').toDate();
        }

        dateHistoryList && setSelectedDatesHistory(dateHistoryList);

        dispatch(financeStore.actionCreators.getPaymentReport(from, to, restaurantId));
    }

    function getIconSource(i: number) {
        if (hoveredSwitchIcon > 0) {
            if (i === 0 && hoveredSwitchIcon === 1) {
                return images.icons.listSelected;
            }
            if (i === 1 && hoveredSwitchIcon === 2) {
                return images.icons.graphSelected;
            }
        }

        switch (chartView) {
            case true:
                switch (i) {
                    case 0:
                        return images.icons.list;
                    case 1:
                        return images.icons.graphSelected;
                }
                break;
            default: {
                switch (i) {
                    case 0:
                        return images.icons.listSelected;
                    case 1:
                        return images.icons.graph;
                }
            }
        }
    }

    function handleDateRangeChange(from: Date | null, to: Date | null): void {
        setDateFrom(from);
        setDateTo(to);
    }

    function exportData(): void {
        var from = dateFrom ?? moment().subtract(1, 'day').toDate();
        var to = dateTo ?? moment().toDate();

        agent.Restaurants.GetPaymentsReport(from, to, restaurantId).then((res) => {
            console.log('res :>> ', res);
        });
    }

    useEffect(() => {
        getData(1);
    }, []);

    function getCheckboxList(): ChartBarCheckbox[] {
        return paymentMethods.map<ChartBarCheckbox>((method) => {
            return {
                id: method,
                name: i18n.t(`finances.paymentMethodEnum.${method.toLowerCase()}`),
                label: <>{i18n.t(`finances.paymentMethodEnum.${method.toLowerCase()}`)}</>,
            };
        });
    }

    return (
        <div className={classes.pBottom5} style={{ height: '100%', overflow: 'auto' }}>
            {!isAdminView && (
                <div className={classes.topContainer} style={{ justifyContent: 'space-between' }}>
                    <StyledChip
                        icon={<InfoOutlinedIcon />}
                        label={i18n.t('finances.filteredByPaymentConfirmationDate')}
                        size="small"
                        labelFontSize={12}
                        labelColor={theme.palette.primary.dark}
                        variant="outlined"
                        color="primary"
                    />
                </div>
            )}

            <div className={classes.kpiContainer}>
                <Box mb={3}>
                    <Grid container spacing={2}>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.totalRevenue')}
                                value={
                                    <AnimatedCounter
                                        value={report.totalAmount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={i18n.t('finances.confirmedPayments')}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.cash')}
                                value={
                                    <AnimatedCounter
                                        value={report.byCash.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={i18n.t('finances.percentAndTransactionsShort', {
                                    percent: report.byCash.sharePercent,
                                    transactions: report.byCash.transactionCount,
                                })}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.cardEft')}
                                value={
                                    <AnimatedCounter
                                        value={report.byCard.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={i18n.t('finances.percentAndTransactionsShort', {
                                    percent: report.byCard.sharePercent,
                                    transactions: report.byCard.transactionCount,
                                })}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.debtor')}
                                value={
                                    <AnimatedCounter
                                        value={report.byDebtor.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={i18n.t('finances.percentAndTransactionsShort', {
                                    percent: report.byDebtor.sharePercent,
                                    transactions: report.byDebtor.transactionCount,
                                })}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.twint')}
                                value={
                                    <AnimatedCounter
                                        value={report.byTwint.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={i18n.t('finances.percentAndTransactionsShort', {
                                    percent: report.byTwint.sharePercent,
                                    transactions: report.byTwint.transactionCount,
                                })}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.tips')}
                                value={
                                    <AnimatedCounter
                                        value={report.totalTipsAmount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={i18n.t('finances.includedInTotal')}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </div>

            <div className={classes.dateContainer}>
                <div className={classes.flexRow}>
                    <DateFilters
                        dateFilter={dateFilter}
                        setDateFilter={setDateFilter}
                        getData={getData}
                        selectedDatesHistory={selectedDatesHistory}
                        setSelectedDatesHistory={setSelectedDatesHistory}
                        isChartsShowing={chartView}
                        onDateRangeChange={handleDateRangeChange}
                    />
                    <span style={{ marginLeft: 20 }}></span>
                    <div className={classes.switchGraphContainer}>
                        <img
                            onClick={() => setChartView(false)}
                            onMouseOver={() => setHoveredSwitchIcon(1)}
                            onMouseLeave={() => setHoveredSwitchIcon(0)}
                            className={classes.switchBasic}
                            src={getIconSource(0)}
                            alt="switch-gaph"
                        />
                        <img
                            onClick={() => {
                                setChartView(true);
                            }}
                            onMouseOver={() => setHoveredSwitchIcon(2)}
                            onMouseLeave={() => setHoveredSwitchIcon(0)}
                            className={classes.switchBasic}
                            src={getIconSource(1)}
                            alt="switch-gaph"
                        />
                    </div>
                    <BasicThreeDotsMenu
                        id="hide-in-print"
                        disabled={true}
                        items={[
                            <div
                                id="hide-in-print"
                                className={classes.threeDotsMenuItemContainer}
                                onClick={() => window.print()}
                            >
                                <img
                                    src={images.icons.print}
                                    alt="print-icon"
                                    className={classes.threeDotsItemIcon}
                                />
                                <ListItemText primary={i18n.t('common.print')} />
                            </div>,
                            <div
                                id="hide-in-print"
                                className={classes.threeDotsMenuItemContainer}
                                onClick={exportData}
                            >
                                <img
                                    src={images.icons.fileDownload}
                                    alt="file-icon"
                                    className={classes.threeDotsItemIcon}
                                />
                                <ListItemText primary={i18n.t('common.excelExport')} />
                            </div>,
                        ]}
                    />
                </div>
            </div>

            {chartView && (
                <Charts
                    currency={currency}
                    selectedDatesHistory={selectedDatesHistory}
                    dateFilter={dateFilter}
                    dataList={dataList}
                    checkboxList={getCheckboxList()}
                    onDateClick={(selectedDates, newDateFilter, dateHistoryList) => {
                        const { startDate, endDate } = selectedDates;
                        setSelectedDatesHistory(dateHistoryList);
                        return dispatch(
                            financeStore.actionCreators.getPaymentReport(
                                startDate,
                                endDate,
                                restaurantId
                            )
                        );
                    }}
                />
            )}
            {!chartView && <PaymentReportTable data={report.payments} currency={currency} />}
        </div>
    );
}

export default PaymentsTab;
